/**
 * Network Monitoring Service
 * Detects and handles network blocking issues
 */

import { FirestoreFallback } from './firestore-retry';

export class NetworkMonitor {
  private static instance: NetworkMonitor;
  private isBlocked = false;
  private listeners: Array<(blocked: boolean) => void> = [];

  static getInstance(): NetworkMonitor {
    if (!NetworkMonitor.instance) {
      NetworkMonitor.instance = new NetworkMonitor();
    }
    return NetworkMonitor.instance;
  }

  private constructor() {
    this.startMonitoring();
  }

  /**
   * Start monitoring for network blocking
   */
  private startMonitoring() {
    // Monitor for common blocking patterns
    const originalFetch = window.fetch;
    const originalXHR = XMLHttpRequest.prototype.open;

    // Override fetch to detect blocking
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        this.setBlocked(false);
        return response;
      } catch (error: any) {
        if (this.isBlockingError(error)) {
          this.setBlocked(true);
        }
        throw error;
      }
    };

    // Override XMLHttpRequest to detect blocking
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      const xhr = this;
      const originalSend = xhr.send;
      
      xhr.send = function(...sendArgs) {
        xhr.addEventListener('error', (event) => {
          const error = event as any;
          if (NetworkMonitor.getInstance().isBlockingError(error)) {
            NetworkMonitor.getInstance().setBlocked(true);
          }
        });
        
        return originalSend.apply(this, sendArgs);
      };
      
      return originalXHR.call(this, method, url, ...args);
    };
  }

  /**
   * Check if error indicates blocking
   */
  private isBlockingError(error: any): boolean {
    const message = error?.message?.toLowerCase() || '';
    const name = error?.name?.toLowerCase() || '';
    
    return (
      message.includes('blocked') ||
      message.includes('failed to fetch') ||
      message.includes('network error') ||
      name.includes('network') ||
      error?.code === 'ERR_BLOCKED_BY_CLIENT' ||
      error?.type === 'blocked'
    );
  }

  /**
   * Set blocked status and notify listeners
   */
  private setBlocked(blocked: boolean) {
    if (this.isBlocked !== blocked) {
      this.isBlocked = blocked;
      console.log(`Network status changed: ${blocked ? 'BLOCKED' : 'UNBLOCKED'}`);
      
      // Notify all listeners
      this.listeners.forEach(listener => listener(blocked));
      
      // Show user notification if blocked
      if (blocked) {
        this.showBlockingNotification();
      }
    }
  }

  /**
   * Show blocking notification to user
   */
  private showBlockingNotification() {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'network-blocking-notification';
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #fef3c7;
        border: 1px solid #f59e0b;
        border-radius: 8px;
        padding: 12px 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 10000;
        max-width: 300px;
        font-size: 14px;
        color: #92400e;
      ">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <strong>Network Blocked</strong>
        </div>
        <div style="margin-bottom: 8px;">
          Your browser is blocking Firebase requests. Data is being saved locally.
        </div>
        <div style="display: flex; gap: 8px;">
          <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
            background: #3b82f6;
            color: white;
            border: none;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
          ">Try Incognito</button>
          <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
            background: #6b7280;
            color: white;
            border: none;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
          ">Dismiss</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  }

  /**
   * Add listener for blocking status changes
   */
  addListener(listener: (blocked: boolean) => void) {
    this.listeners.push(listener);
  }

  /**
   * Remove listener
   */
  removeListener(listener: (blocked: boolean) => void) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Get current blocked status
   */
  isCurrentlyBlocked(): boolean {
    return this.isBlocked;
  }

  /**
   * Test network connectivity
   */
  async testConnectivity(): Promise<boolean> {
    try {
      // Test with a simple, reliable endpoint instead of Firestore
      const response = await fetch('https://www.google.com', {
        method: 'HEAD',
        mode: 'no-cors'
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get fallback data when Firestore is blocked
   */
  async getFallbackData(userId: string, collection: string): Promise<any[]> {
    try {
      return await FirestoreFallback.getAllFromLocalStorage(`${collection}_${userId}`);
    } catch (error) {
      console.error('Failed to get fallback data:', error);
      return [];
    }
  }
}
